import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { getDashboard, getEarnings, getStoreEarnings } from "../../api/deliveryCompanyService";
import { formatCurrency } from "../Admin/constants";

const OVERVIEW_CARDS = [
  { key: "driverCount", label: "Drivers", icon: PeopleAltOutlinedIcon, color: "primary" },
  { key: "activeDeliveryCount", label: "Active Deliveries", icon: LocalShippingOutlinedIcon, color: "info" },
  { key: "completedDeliveryCount", label: "Completed Deliveries", icon: CheckCircleOutlineIcon, color: "success" },
];

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "currentMonth", label: "Current Month" },
  { value: "pastMonth", label: "Past Month" },
];

function MoneyCell({ total, count }) {
  return (
    <Stack sx={{ lineHeight: 1.1, py: 1 }}>
      <Typography variant="body2">{formatCurrency(total)}</Typography>
      <Typography variant="caption" color="text.secondary">
        {count} {count === 1 ? "order" : "orders"}
      </Typography>
    </Stack>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: `${color}.main`, width: 48, height: 48 }}>
            <Icon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h4">{value}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [period, setPeriod] = useState("currentMonth");
  const [dashboard, setDashboard] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [storeEarnings, setStoreEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getDashboard(period), getEarnings(period), getStoreEarnings(period)])
      .then(([dashboardData, earningsData, storeEarningsData]) => {
        if (!active) return;
        setDashboard(dashboardData);
        setEarnings(earningsData);
        setStoreEarnings(storeEarningsData);
        setError("");
      })
      .catch(() => {
        if (active) setError("Could not load dashboard data. Is the API running?");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [period]);

  const periodLabel = PERIOD_OPTIONS.find((option) => option.value === period)?.label ?? "Current Month";

  const handlePeriodChange = (event, value) => {
    if (value) setPeriod(value);
  };

  const driverColumns = useMemo(
    () => [
      { field: "fullName", headerName: "Driver", flex: 1, minWidth: 160 },
      {
        field: "periodTotal",
        headerName: periodLabel,
        width: 150,
        renderCell: (params) => <MoneyCell total={params.row.periodTotal} count={params.row.periodCount} />,
      },
      {
        field: "allTimeTotal",
        headerName: "All Time",
        width: 130,
        renderCell: (params) => <MoneyCell total={params.row.allTimeTotal} count={params.row.allTimeCount} />,
      },
    ],
    [periodLabel]
  );

  const storeColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180, sortable: false },
      { field: "orderCount", headerName: "Orders", width: 100, sortable: false },
      {
        field: "deliveryFeeTotal",
        headerName: "Delivery Fee Total",
        width: 170,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "commissionPercent",
        headerName: "Platform Fee %",
        width: 130,
        sortable: false,
        valueFormatter: (v) => (v == null ? "" : `${v}%`),
      },
      {
        field: "platformFeeTotal",
        headerName: "Platform Fee",
        width: 140,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "myEarningsTotal",
        headerName: "My Earnings",
        width: 150,
        sortable: false,
        valueFormatter: (v) => formatCurrency(v),
      },
    ],
    []
  );

  const storeRows = useMemo(() => {
    if (storeEarnings.length === 0) return [];

    const totals = storeEarnings.reduce(
      (acc, s) => ({
        orderCount: acc.orderCount + (s.orderCount || 0),
        deliveryFeeTotal: acc.deliveryFeeTotal + (s.deliveryFeeTotal || 0),
        platformFeeTotal: acc.platformFeeTotal + (s.platformFeeTotal || 0),
        myEarningsTotal: acc.myEarningsTotal + (s.myEarningsTotal || 0),
      }),
      { orderCount: 0, deliveryFeeTotal: 0, platformFeeTotal: 0, myEarningsTotal: 0 }
    );

    return [
      ...storeEarnings,
      {
        storeId: "__total__",
        storeName: "Total",
        commissionPercent: null,
        isTotalRow: true,
        ...totals,
      },
    ];
  }, [storeEarnings]);

  const periodToggle = (
    <ToggleButtonGroup value={period} exclusive onChange={handlePeriodChange} size="small" color="primary">
      {PERIOD_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  const header = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", sm: "center" }}
      spacing={2}
      sx={{ mb: 3 }}
    >
      <Box>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Dashboard
        </Typography>
        {dashboard?.companyName && (
          <Typography variant="body2" color="text.secondary">
            {dashboard.companyName}
          </Typography>
        )}
      </Box>
      {periodToggle}
    </Stack>
  );

  if (loading) {
    return (
      <Box>
        {header}
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {header}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!dashboard || !earnings) return null;

  return (
    <Box>
      {header}

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {OVERVIEW_CARDS.map(({ key, label, icon, color }) => (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <StatCard icon={icon} label={label} value={dashboard[key]} color={color} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <StatCard
            icon={ReceiptLongOutlinedIcon}
            label={`Orders Delivered (${periodLabel})`}
            value={earnings.periodCount}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard
            icon={PaidOutlinedIcon}
            label={`Delivery Fees (${periodLabel})`}
            value={formatCurrency(earnings.periodTotal)}
            color="primary"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Earnings By Driver
      </Typography>
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, mb: 4 }}>
        <DataGrid
          autoHeight
          rows={earnings.drivers ?? []}
          getRowId={(row) => row.userId}
          columns={driverColumns}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          hideFooter={(earnings.drivers ?? []).length <= 100}
          sx={{ border: "none" }}
        />
        {(earnings.drivers ?? []).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            No drivers yet.
          </Typography>
        )}
      </Box>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Earnings By Store
      </Typography>
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={storeRows}
          getRowId={(row) => row.storeId}
          columns={storeColumns}
          hideFooter={storeRows.length <= 100}
          disableRowSelectionOnClick
          getRowClassName={(params) => (params.row.isTotalRow ? "total-row" : "")}
          sx={{
            border: "none",
            "& .total-row": {
              bgcolor: "action.hover",
              fontWeight: 700,
              "& .MuiDataGrid-cell": { fontWeight: 700 },
            },
          }}
        />
        {storeEarnings.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            No store deliveries yet.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
